// src/pages/PaperView.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { motion } from "framer-motion";
import {
  HiArrowLeft,
  HiDocumentText,
  HiAcademicCap,
  HiBookOpen,
  HiCalendar,
  HiEye,
  HiChat,
} from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import { db } from "../components/firebase/firebase";
import Loader from "../components/common/Loader";
import LogoImg from "../components/assets/images/logos-ravi.png";
import { SEO } from "../components/common/SEO";
import SaveButton from "../components/student/SaveButton";
import ProtectedPdfViewer from "../components/common/ProtectedPdfViewer";

const WHATSAPP_NUMBER = "918056206308";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };

// Uploads store the board in `type`; older docs may use `board`.
const paperBoard = (paper) => paper.board || paper.type || "";
const prettySubject = (s = "") => s.replace(/-+/g, " ").trim();

const BOARD_COLORS = {
  state:       { chip: "bg-blue-50 text-blue-700 border-blue-100",      dot: "bg-blue-500" },
  cbse:        { chip: "bg-indigo-50 text-indigo-700 border-indigo-100", dot: "bg-indigo-500" },
  competitive: { chip: "bg-sky-50 text-sky-700 border-sky-100",         dot: "bg-sky-500" },
};

function buildWhatsAppUrl(paper) {
  const board = paperBoard(paper);
  const lines = [
    "Hi! I need the answers for this question paper 📄",
    "",
    `*Title:* ${paper.title || "Untitled"}`,
    paper.refId   ? `*Ref ID:* #${paper.refId}` : null,
    paper.subject ? `*Subject:* ${paper.subject}` : null,
    paper.grade   ? `*Class:* ${paper.grade}` : null,
    board         ? `*Board:* ${BOARD_LABELS[board] || board}` : null,
    (paper.month || paper.year) ? `*Date:* ${paper.month || ""} ${paper.year || ""}`.trim() : null,
    "",
    "Please send me the paper and answers. Thank you!",
  ].filter((l) => l !== null);

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export default function PaperView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, "resources", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) { if (!cancelled) setNotFound(true); return; }
        const data = { id: snap.id, ...snap.data() };
        if (!cancelled) setPaper(data);
        // Count the view in the background — don't hold the page on this write.
        updateDoc(ref, { views: increment(1) }).catch(console.error);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <Loader />;

  if (notFound || !paper) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-gray-50">
        <SEO title="Paper not found" noIndex />
        <div className="text-7xl mb-5">📄</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Paper not found</h2>
        <p className="text-gray-400 mb-6 text-sm">This paper may have been removed or the link is incorrect.</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-6 py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
        >
          <HiArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const board = paperBoard(paper);
  const boardLabel = BOARD_LABELS[board] || board;
  const subjectLabel = prettySubject(paper.subject);
  const boardStyle = BOARD_COLORS[board] || { chip: "bg-gray-50 text-gray-600 border-gray-100", dot: "bg-gray-400" };
  const paperSeoTitle = [
    paper.title,
    paper.grade ? `Class ${paper.grade}` : "",
    boardLabel,
  ].filter(Boolean).join(" | ");
  const paperSeoDesc = `View ${paper.title || "question paper"}${subjectLabel ? ` — ${subjectLabel}` : ""}${paper.grade ? `, Class ${paper.grade}` : ""}${boardLabel ? `, ${boardLabel}` : ""}. Free on Ravi TestPapers. Need answers? WhatsApp us with Ref #${paper.refId || ""}.`;
  const listingUrl = board && paper.grade && subjectLabel
    ? `/resources/${board}/${paper.grade}/${paper.subject}`
    : null;
  const waUrl = buildWhatsAppUrl(paper);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={paperSeoTitle}
        description={paperSeoDesc}
        canonical={`/paper/${id}`}
        type="article"
      />

      {/* ── Sticky top bar ─────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-gray-600 hover:text-blue-600 font-semibold text-sm transition-colors"
          >
            <HiArrowLeft className="text-base" /> Back
          </button>

          <div className="flex-1 min-w-0 px-2">
            <h1 className="font-bold text-gray-900 text-sm sm:text-base truncate leading-tight">
              {paper.title || "Untitled Paper"}
            </h1>
            {paper.refId && (
              <p className="text-[11px] font-mono text-gray-400 tracking-wider">#{paper.refId}</p>
            )}
          </div>

          <SaveButton kind="paper" item={paper} />

          {/* WhatsApp shortcut in topbar */}
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm px-4 py-2 rounded-xl transition-colors shadow-md shadow-blue-200"
          >
            <FaWhatsapp className="text-base" />
            <span className="hidden sm:inline">Need Answers?</span>
            <span className="sm:hidden">Answers</span>
          </a>
        </div>
      </div>

      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">

        {/* ── Paper meta card ─────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5"
        >
          <div className="flex flex-wrap items-center gap-2">
            {subjectLabel && (
              <span className="inline-flex items-center gap-1.5 bg-pink-50 text-pink-700 border border-pink-100 text-xs font-semibold px-3 py-1.5 rounded-full capitalize">
                <HiBookOpen className="text-xs" /> {subjectLabel}
              </span>
            )}
            {board && (
              <span className={`inline-flex items-center gap-1.5 border text-xs font-semibold px-3 py-1.5 rounded-full ${boardStyle.chip}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${boardStyle.dot}`} />
                {boardLabel}
              </span>
            )}
            {paper.grade && (
              <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-semibold px-3 py-1.5 rounded-full">
                <HiAcademicCap className="text-xs" /> Class {paper.grade}
              </span>
            )}
            {(paper.month || paper.year) && (
              <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-600 border border-gray-100 text-xs font-medium px-3 py-1.5 rounded-full">
                <HiCalendar className="text-xs" />
                {paper.month || ""} {paper.year || ""}
              </span>
            )}
            {paper.refId && (
              <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-100 text-xs font-mono px-3 py-1.5 rounded-full">
                <HiDocumentText className="text-xs" /> #{paper.refId}
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-1 text-gray-400 text-xs">
              <HiEye /> {paper.views || 0} views
            </span>
          </div>
        </motion.div>

        {/* ── Paid Answers CTA card ───────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 sm:p-8 text-white shadow-xl shadow-blue-500/25 mb-5"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Icon + text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <HiChat className="text-2xl text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-black text-lg leading-tight">Need the Answers?</h3>
                  <span className="bg-yellow-400 text-yellow-900 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide">
                    Paid
                  </span>
                </div>
                <p className="text-blue-100 text-sm leading-relaxed">
                  WhatsApp us — we'll send you the full paper + answer key.
                  Your reference number is auto-filled in the message.
                </p>
                {paper.refId && (
                  <p className="mt-2 text-[11px] font-mono bg-white/20 inline-block px-2.5 py-1 rounded-lg tracking-wider">
                    Ref #{paper.refId}
                  </p>
                )}
              </div>
            </div>

            {/* Button */}
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2.5 bg-white text-blue-700 font-black px-6 py-3.5 rounded-xl hover:bg-blue-50 transition-colors shadow-lg text-sm sm:text-base"
            >
              <FaWhatsapp className="text-xl text-green-500" />
              Send on WhatsApp
            </a>
          </div>

          {/* Steps */}
          <div className="mt-6 pt-5 border-t border-white/20 grid grid-cols-3 gap-3 text-center">
            {[
              { n: "1", text: "Tap the button to open WhatsApp" },
              { n: "2", text: "Send the pre-filled message" },
              { n: "3", text: "Receive paper + answers from us" },
            ].map((s) => (
              <div key={s.n} className="flex flex-col items-center gap-1.5">
                <div className="w-7 h-7 rounded-full bg-white/25 flex items-center justify-center text-xs font-black">{s.n}</div>
                <p className="text-blue-100 text-[11px] leading-snug">{s.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── PDF viewer (view-only) ──────────────────────── */}
        {paper.link ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.18 }}
          >
            <ProtectedPdfViewer url={paper.link} title={paper.title} />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.18 }}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center gap-4 py-20 text-center"
          >
            <img src={LogoImg} alt="" className="w-20 h-20 object-contain opacity-30" />
            <p className="text-gray-500 font-semibold">Preview unavailable</p>
            <p className="text-gray-400 text-sm max-w-xs">
              Use the WhatsApp button above to request this paper and its answers.
            </p>
          </motion.div>
        )}

        {/* ── About this paper (crawlable text + internal link) ── */}
        <section className="mt-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-sm text-gray-600 leading-relaxed">
          <h2 className="font-bold text-gray-800 text-base mb-1.5">About this paper</h2>
          <p>
            {paper.title || "This question paper"} is a free
            {boardLabel ? ` ${boardLabel}` : ""}
            {paper.grade ? ` Class ${paper.grade}` : ""}
            {subjectLabel ? <> <span className="capitalize">{subjectLabel}</span></> : ""} paper
            {(paper.month || paper.year) ? ` from ${[paper.month, paper.year].filter(Boolean).join(" ")}` : ""}.
            View it online here for exam practice. For the answer key, message us on WhatsApp
            {paper.refId ? ` with reference #${paper.refId}` : ""}.
          </p>
          {listingUrl && (
            <Link to={listingUrl} className="inline-block mt-3 font-semibold text-blue-600 hover:text-blue-800">
              More Class {paper.grade} <span className="capitalize">{subjectLabel}</span> papers →
            </Link>
          )}
        </section>

      </div>
    </div>
  );
}
